import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Check, Copy } from 'lucide-react';
import { memo, useState } from 'react';

import { ctw } from '@/common';

const sizeVariants = cva(null, {
  variants: {
    size: {
      small: 'h-2 w-2',
      default: 'h-3 w-3',
      large: 'h-4 w-4',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

type CopyButtonProps = {
  textToCopy: string;
  className?: string;
} & VariantProps<typeof sizeVariants>;

export const CopyButton = memo<CopyButtonProps>(({ textToCopy, className, size }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={ctw('text-gray-400 hover:text-gray-600 transition-colors', className)}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <Check className={ctw(sizeVariants({ size }), 'text-green-500')} />
      ) : (
        <Copy className={ctw(sizeVariants({ size }))} />
      )}
    </button>
  );
});

CopyButton.displayName = 'CopyButton';
